import React, { useState, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Template } from "@/types";
import JoditEditor from "jodit-react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { MessageSquareIcon, AtSignIcon, MailIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// const formatHours = (hours: number) => {
//   if (hours < 24) {
//     return `${hours} hour${hours !== 1 ? "s" : ""}`;
//   } else {
//     const days = Math.floor(hours / 24);
//     const remainingHours = hours % 24;
//     return `${days} day${days !== 1 ? "s" : ""}${
//       remainingHours > 0
//         ? ` ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`
//         : ""
//     }`;
//   }
// };

const Templates: React.FC = () => {
  const { toast } = useToast();
  const selectedMover = useSelector(getSelectedMover);
  const [updateMover, { isLoading }] = useUpdateMoverMutation();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const editorRef = useRef(null);

  const { control, getValues } = useForm<{
    templates: Template[];
  }>({
    defaultValues: { templates: selectedMover?.templates || [] },
  });

  const { fields, update, remove, prepend } = useFieldArray({
    control,
    name: "templates",
    keyName: "_id",
  });

  const validateTemplate = (template: Template): string[] => {
    const errors: string[] = [];

    if (!template.name.trim()) {
      errors.push("Template name is required");
    }

    if (template.channel === "email") {
      if (!template.subject?.trim()) {
        errors.push("Subject is required for email templates");
      }
    }

    if (!template.body.trim()) {
      errors.push("Body is required");
    }

    return errors;
  };

  const onSubmit = async (data: { template: Template }) => {
    const validationErrors = validateTemplate(data.template);

    if (validationErrors.length > 0) {
      toast({
        title: "Validation Error",
        description: validationErrors.join(", "),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateMover({
        data: {
          templates_attributes: [data.template],
        },
      }).unwrap();

      toast({
        title: "Success",
        description: "Templates updated successfully",
      });

      setEditingIndex(null);
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Failed to update templates",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleDelete = async (index: number) => {
    const template = getValues().templates[index];
    try {
      await updateMover({
        data: {
          templates_attributes: [
            {
              id: template.id,
              _destroy: true,
            },
          ],
        },
      }).unwrap();

      remove(index);

      toast({
        title: "Success",
        description: "Template deleted successfully",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    }
  };

  const handleSave = async (index: number) => {
    const currentData = getValues();
    const template = currentData.templates[index];

    const validationErrors = validateTemplate(template);

    if (validationErrors.length > 0) {
      toast({
        title: "Validation Error",
        description: validationErrors.join(", "),
        variant: "destructive",
      });
      return;
    }

    update(index, template);

    await onSubmit({ template });
  };

  const handleAddNew = (channel: "email" | "text") => {
    const newTemplate = {
      id: null,
      name: "",
      subject: "",
      body: "",
      channel: channel,
      category: "customer_communications",
    };

    if (getValues().templates[0]?.id === null) {
      remove(0);
    }

    prepend(newTemplate);
    setEditingIndex(0);

    // Update the indices of existing templates
    const currentData = getValues();
    const updatedTemplates = currentData.templates.map((template, idx) => ({
      ...template,
      position: idx + 1,
    }));

    // Update the form with the new template order
    update(0, newTemplate);
    updatedTemplates.forEach((template, idx) => {
      if (idx > 0) {
        update(idx, template);
      }
    });
  };

  const config = {
    readonly: false,
    placeholder: "Start typing...",
    buttons: ["bold", "italic", "underline", "|", "ul", "ol", "|", "link"],
    toolbarAdaptive: false,
    height: 200,
    fontSize: "14px",
    cleanHTML: {
      fillEmptyParagraph: false,
      removeEmptyElements: true,
    },
  };

  if (!selectedMover) return null;

  return (
    <div className="space-y-8 w-full p-4">
      <div className="w-full lg:w-3/4 flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-baseline w-full">
        <div className="flex flex-col justify-start items-start gap-2">
          <h2 className="text-2xl font-bold">Templates</h2>
          <p className="w-3/4">
            Add email and text message templates to help with customer
            follow-ups.
          </p>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="flex items-center gap-1"
            onClick={() => handleAddNew("email")}
          >
            <AtSignIcon className="h-4 w-4" />
            Add Email Template
          </Button>
          <Button
            className="flex items-center gap-1"
            onClick={() => handleAddNew("text")}
          >
            <MessageSquareIcon className="h-4 w-4" />
            Add Text Template
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row-reverse lg:items-start gap-4">
        <div className="w-full lg:w-1/4 p-4 bg-stone-50 border border-stone-200">
          <h4 className="text-base font-bold">Tags</h4>
          <p className="text-sm text-gray-700">
            You can use tags to personalize your texts and emails to customers.{" "}
            <span className="underline underline-offset-2">
              Please copy/paste the tag exactly as shown.
            </span>
          </p>
          <ul className="mt-4 list-none p-0 m-0 text-sm">
            <li>
              <span className="font-mono font-semibold">#customerfullname</span>{" "}
              <span className="">Customer's full name</span>
            </li>

            <li>
              <span className="font-mono font-semibold">
                #customerfirstname
              </span>{" "}
              <span className="">Customer's first name</span>
            </li>

            <li>
              <span className="font-mono font-semibold">#companyname</span>{" "}
              <span className="">Your company name</span>
            </li>

            <li>
              <span className="font-mono font-semibold">#companyphone</span>{" "}
              <span className="">Your business phone</span>
            </li>

            <li>
              <span className="font-mono font-semibold">#companyemail</span>{" "}
              <span className="">Your business email</span>
            </li>

            <li>
              <span className="font-mono font-semibold">#companywebsite</span>{" "}
              <span className="">Your business website</span>
            </li>

            <li>
              <span className="font-mono font-semibold">#ownerfirstname</span>{" "}
              <span className="">
                Your company owner's first name. Use this to personalize the
                message.
              </span>
            </li>

            <li>
              <span className="font-mono font-semibold">#movedate</span>{" "}
              <span className="">
                Service date (we will show it as mm/dd/yyyy){" "}
              </span>
            </li>

            <li>
              <span className="font-mono font-semibold">#movecities</span>{" "}
              <span className="">
                From - To City (we will format this depending on the move type).
                For eg Austin to Dallas.
              </span>
            </li>

            {/* <li>
              <span className="font-mono font-semibold">#customerhublink</span>{" "}
              <span className="">Link to customer hub</span>
            </li> */}

            <li>
              <span className="font-mono font-semibold">#repfullname</span>{" "}
              <span className="">
                Full name of the rep sending the email/text. Use this to
                personalize the message.
              </span>
            </li>

            <li>
              <span className="font-mono font-semibold">#repfirstname</span>{" "}
              <span className="">
                First name of the rep sending the email/text. Use this to
                personalize the message.
              </span>
            </li>
          </ul>
        </div>
        <div className="w-full lg:w-3/4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {fields.length === 0 && <p>You haven't setup any templates yet.</p>}
          {fields.map((field, index) => (
            <Card
              className={cn(
                "col-span-1 rounded-none shadow-none relative",
                editingIndex === index && "border-2 border-amber-500"
              )}
              key={field.id}
            >
              <CardHeader className="flex flex-row justify-between items-start space-y-0 pb-2">
                <div className="flex flex-col gap-1">
                  {getValues().templates[index].channel === "email" ? (
                    <div className="flex flex-row items-center gap-1 text-sm font-semibold text-sky-700">
                      <MailIcon className="h-3 w-3" />
                      <p>Email</p>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-1 text-sm font-semibold text-sky-700">
                      <MessageSquareIcon className="h-3 w-3" />
                      <p>Text</p>
                    </div>
                  )}
                  <div className="text-base font-medium">
                    {getValues().templates[index].name}
                  </div>
                </div>
                <div className="flex gap-2">
                  {editingIndex === index ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleSave(index)}
                        disabled={isLoading}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (field.id === null) {
                            remove(index);
                          }

                          setEditingIndex(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(index)}
                      >
                        Edit
                      </Button>
                      {field.id !== null && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() =>
                            window.confirm(
                              "Are you sure you want to delete this template?"
                            ) && handleDelete(index)
                          }
                        >
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {editingIndex === index && (
                  <Controller
                    name={`templates.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        required
                        {...field}
                        className="w-full"
                        placeholder="Give the template a name eg 'New Lead', 'Discount Offer'"
                      />
                    )}
                  />
                )}
                {field.channel === "email" && (
                  <div className="mb-2">
                    {editingIndex === index ? (
                      <Controller
                        name={`templates.${index}.subject`}
                        control={control}
                        render={({ field }) => (
                          <Input {...field} placeholder="Email Subject" />
                        )}
                      />
                    ) : (
                      <p className="text-sm font-medium">{field.subject}</p>
                    )}
                  </div>
                )}
                <div className="mt-4">
                  {editingIndex === index ? (
                    <Controller
                      name={`templates.${index}.body`}
                      control={control}
                      render={({ field }) => (
                        <div>
                          {getValues().templates[index].channel === "email" ? (
                            <JoditEditor
                              ref={editorRef}
                              value={field.value}
                              config={config}
                              onBlur={field.onChange}
                            />
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Textarea
                                  {...field}
                                  placeholder="Enter your message"
                                  className="min-h-[100px]"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    />
                  ) : (
                    <div
                      className="text-sm prose max-w-none max-h-[200px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: field.body }}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Templates;
